import { ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useAvailableCallerIds } from '@/api/agents'
import { useProfile } from '@/api/profile'
import { useTrustCenter } from '@/api/trustCenter'
import { cn } from '@/lib/utils'

/**
 * Bandeau au-dessus du champ texto : prévient AVANT d'écrire quand la ligne
 * choisie ne peut pas texter ce destinataire (sans-frais non vérifié, ou local
 * vers un numéro américain sans enregistrement). Se décide d'après la ligne
 * sélectionnée dans le téléphone à cet instant, pas d'après le compte.
 */
export function SmsLineNotice({ selectedCallerId, toNumber, darkMode, className }: { selectedCallerId: string | null; toNumber: string; darkMode?: boolean; className?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const { data: lignes = [] } = useAvailableCallerIds()
  const { data: profile } = useProfile()
  const ligne = lignes.find((n) => n.phone_number === selectedCallerId)
  const versEtatsUnis = estAmericain(toNumber)
  const { data: confiance } = useTrustCenter(versEtatsUnis ? profile?.company : undefined)

  let message: string | null = null
  if (ligne?.is_toll_free && ligne.tollfree_sms_status !== 'approved') {
    message = t(ligne.tollfree_sms_status === 'pending' ? 'sms.notice.tollfreePending' : 'sms.notice.tollfree')
  } else if (versEtatsUnis && (!ligne || !ligne.is_toll_free) && confiance && confiance.us.status !== 'approved') {
    message = t(confiance.us.status === 'pending' ? 'sms.notice.usPending' : 'sms.notice.us')
  }
  if (!message) return null

  return (
    <div className={cn(className ?? 'mx-4', 'mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs',
      darkMode ? 'bg-amber-500/10 text-amber-200 border border-amber-500/30' : 'bg-amber-50 text-amber-800 border border-amber-200')}>
      <span className="flex items-center gap-2"><ShieldAlert className="h-3.5 w-3.5 shrink-0" />{message}</span>
      <button type="button" onClick={() => navigate(`/${locale}/manager?view=trust`)} className="font-semibold underline underline-offset-2">
        {t('sms.notice.cta')}
      </button>
    </div>
  )
}

/** Indicatifs nord-américains hors Canada : approximation côté client, le serveur tranche. */
const CANADA = new Set(['204', '226', '236', '249', '250', '263', '289', '306', '343', '354', '365', '367', '368', '382', '387', '403', '416', '418', '428', '431', '437', '438', '450', '460', '468', '474', '506', '514', '519', '548', '579', '581', '584', '587', '604', '613', '639', '647', '672', '683', '705', '709', '742', '753', '778', '780', '782', '807', '819', '825', '867', '873', '879', '902', '905'])
const SANS_FRAIS = new Set(['800', '833', '844', '855', '866', '877', '888'])
function estAmericain(n: string) {
  const d = (n || '').replace(/\D/g, '')
  if (d.length !== 11 || d[0] !== '1') return false
  const ind = d.slice(1, 4)
  return !CANADA.has(ind) && !SANS_FRAIS.has(ind)
}
